package br.com.heyagenda.data.repository

import br.com.heyagenda.data.network.PlatformHttp
import br.com.heyagenda.domain.repository.AuthRepository
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

data class WhatsAppStatus(
    val connected: Boolean,
    val phone: String,
    val status: String? = null
)

data class PairingResult(
    val pairingCode: String?,
    val alreadyConnected: Boolean,
    val phone: String
)

interface WhatsAppRepository {
    suspend fun getConnectionStatus(): Result<WhatsAppStatus>
    suspend fun requestPairingCode(phone: String? = null): Result<PairingResult>
}

class WhatsAppRepositoryImpl(
    private val authRepository: AuthRepository
) : WhatsAppRepository {

    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
    }

    override suspend fun getConnectionStatus(): Result<WhatsAppStatus> {
        val idToken = authRepository.getIdToken(forceRefresh = false)
            ?: return Result.failure(Exception("Sessão expirada. Faça login novamente."))

        val result = PlatformHttp.postJson(
            url = GET_STATUS_URL,
            jsonBody = "{}",
            authToken = idToken
        )

        return result.fold(
            onSuccess = { responseBody ->
                try {
                    val root = json.parseToJsonElement(responseBody).jsonObject
                    val connected = root["connected"]?.jsonPrimitive?.booleanOrNull ?: false
                    val phone = root["phone"]?.jsonPrimitive?.content ?: ""
                    val status = root["status"]?.jsonPrimitive?.content
                    Result.success(WhatsAppStatus(connected = connected, phone = phone, status = status))
                } catch (e: Exception) {
                    Result.failure(Exception("Erro ao processar resposta do servidor."))
                }
            },
            onFailure = { error ->
                val friendlyMessage = extractErrorMessage(error.message)
                Result.failure(Exception(friendlyMessage))
            }
        )
    }

    override suspend fun requestPairingCode(phone: String?): Result<PairingResult> {
        val idToken = authRepository.getIdToken(forceRefresh = false)
            ?: return Result.failure(Exception("Sessão expirada. Faça login novamente."))

        val payload = if (!phone.isNullOrBlank()) {
            """{"phone":"${escapeJson(phone)}"}"""
        } else {
            "{}"
        }

        val result = PlatformHttp.postJson(
            url = REQUEST_PAIRING_URL,
            jsonBody = payload,
            authToken = idToken
        )

        return result.fold(
            onSuccess = { responseBody ->
                try {
                    val root = json.parseToJsonElement(responseBody).jsonObject
                    val pairingCode = root["pairingCode"]?.jsonPrimitive?.content
                    val alreadyConnected = root["alreadyConnected"]?.jsonPrimitive?.booleanOrNull ?: false
                    val resolvedPhone = root["phone"]?.jsonPrimitive?.content ?: (phone ?: "")

                    if (pairingCode == null && !alreadyConnected) {
                        Result.failure(Exception("Não foi possível obter o código de pareamento. Tente novamente."))
                    } else {
                        Result.success(
                            PairingResult(
                                pairingCode = pairingCode,
                                alreadyConnected = alreadyConnected,
                                phone = resolvedPhone
                            )
                        )
                    }
                } catch (e: Exception) {
                    Result.failure(Exception("Erro ao processar resposta do servidor."))
                }
            },
            onFailure = { error ->
                val friendlyMessage = extractErrorMessage(error.message)
                Result.failure(Exception(friendlyMessage))
            }
        )
    }

    private fun escapeJson(value: String): String {
        return value.replace("\\", "\\\\")
            .replace("\"", "\\\"")
            .replace("\n", "\\n")
            .replace("\r", "\\r")
    }

    private fun extractErrorMessage(rawError: String?): String {
        if (rawError.isNullOrBlank()) return "Não foi possível conectar com o servidor. Tente novamente."
        return try {
            val root = json.parseToJsonElement(rawError.substringAfter("HTTP ").substringAfter(": ")).jsonObject
            root["error"]?.jsonPrimitive?.content ?: "Ocorreu um erro ao comunicar com o WhatsApp."
        } catch (_: Exception) {
            if (rawError.contains("401")) {
                "Sua sessão expirou. Faça login novamente."
            } else {
                "Não foi possível conectar com o servidor. Tente novamente."
            }
        }
    }

    companion object {
        private const val GET_STATUS_URL = "https://getwhatsappstatus-unb2lf6tea-uc.a.run.app"
        private const val REQUEST_PAIRING_URL = "https://requestpairingcode-unb2lf6tea-uc.a.run.app"
    }
}
